import React from "react"
import { useTranslation } from "react-i18next"
import { Link, NavLink, useNavigate } from "react-router"

import { Page404 } from "@/components/nav/page404"
import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import { useAuthentication } from "@/contexts"
import { AdminEntityFieldsSkeleton } from "@/modules/admin/components/admin-entity-fields-skeleton"
import {
  UserReadonlyFields,
  canDeleteAdminUser,
  canMutateAdminUser,
  useAdminUserPageState,
  useConfirmDeleteUser,
} from "@/modules/admin/users"
import { useUrl } from "@/utils/url"

import { Button, Link as MuiLink, Stack } from "@mui/material"

export const AdminUserRoute: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { activeUser, isAdminWriterUser, isOrgAdminUser } = useAuthentication()
  const { adminUserEditPath, adminUsersPath } = useUrl()
  const { userData, userId, userIsLoading, userIsUnavailable } =
    useAdminUserPageState()

  const canMutateUser = canMutateAdminUser({
    isAdminWriterUser,
    isOrgAdminUser,
    targetRole: userData?.role,
  })
  const canDeleteUser = canDeleteAdminUser({
    actorId: activeUser?.id,
    isAdminWriterUser,
    isOrgAdminUser,
    targetId: userId,
    targetRole: userData?.role,
  })

  const { confirmAndDeleteUser, deleteUserIsPending } = useConfirmDeleteUser({
    onSuccess: () => {
      void navigate(adminUsersPath, { replace: true })
    },
  })

  if (userIsUnavailable) {
    return <Page404 />
  }

  const handleDeleteClick = () => {
    if (!userData?.id || !canDeleteUser) {
      return
    }

    confirmAndDeleteUser({
      firstName: userData.firstName,
      id: userData.id,
      lastName: userData.lastName,
    }).catch(() => undefined)
  }

  let userFieldsContent: React.ReactNode = null
  if (userIsLoading) {
    userFieldsContent = (
      <AdminEntityFieldsSkeleton
        ariaLabel={t("loadingAriaLabel", {
          title: t("admin.userDetail.title"),
        })}
      />
    )
  } else if (userData != null) {
    userFieldsContent = <UserReadonlyFields user={userData} />
  }

  return (
    <Stack sx={{ gap: 2 }}>
      <PageTitle
        breadcrumbActions={
          <MuiLink component={Link} to={adminUsersPath}>
            {t("admin.users")}
          </MuiLink>
        }
        heading={t("admin.userDetail.title")}
        primaryActions={
          canMutateUser ? (
            <Button
              component={NavLink}
              disabled={userIsLoading}
              to={adminUserEditPath(String(userId))}
              variant="contained"
            >
              {t("button.edit")}
            </Button>
          ) : undefined
        }
        secondaryActions={
          canDeleteUser ? (
            <Button
              color="error"
              disabled={deleteUserIsPending || userIsLoading}
              onClick={handleDeleteClick}
            >
              {t("button.delete")}
            </Button>
          ) : undefined
        }
      />
      <PageCard>
        <Stack sx={{ gap: 2 }}>{userFieldsContent}</Stack>
      </PageCard>
    </Stack>
  )
}
